// What an .xlsx becomes when read, and what is written back (design 02
// 12.1). Cells carry the input as a person would type it, like the server's
// grid cells: "=SUM(A1:A3)" (formula), "1000" (number), "'007" (text that
// would otherwise read as something else), "TRUE", "#N/A".

export interface XStyle {
  nf?: string; // number format, Excel's code
  b?: boolean;
  i?: boolean;
  u?: boolean;
  fg?: string; // #rrggbb
  bg?: string;
  al?: "l" | "c" | "r";
  wrap?: boolean;
}

export interface XCell {
  row: number; // from 0
  col: number;
  input: string;
  style?: XStyle;
}

export interface XSheet {
  name: string;
  cells: XCell[];
  colWidths: Record<string, number>; // column (from 0) -> px
  frozenRows: number;
  frozenCols: number;
}

export interface XName {
  name: string;
  formula: string; // without "="
}

export interface XBook {
  sheets: XSheet[];
  names: XName[];
  /** what was left out, in words for the person (charts, pivots, ...) */
  skipped: string[];
}

// the server's limits (server/src/tensor/tensor.h)
export const GRID_ROWS = 1_048_576;
export const GRID_COLS = 16_384;
export const SHEETS_MAX = 200;
export const CELLS_MAX = 1_000_000;
export const CELL_INPUT_MAX_BYTES = 8192;
export const NAMES_MAX = 1000;
export const NF_MAX = 64;
export const FROZEN_ROWS_MAX = 100;
export const FROZEN_COLS_MAX = 50;
export const COL_WIDTH_MIN = 10;
export const COL_WIDTH_MAX = 2000;

export function colName(col: number): string {
  let s = "";
  let n = col + 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

export function cellName(row: number, col: number): string {
  return colName(col) + String(row + 1);
}

/** "AB12" -> {row: 11, col: 27}; null when it is not a cell */
export function parseCell(ref: string): { row: number; col: number } | null {
  const m = /^\$?([A-Za-z]{1,3})\$?([0-9]{1,7})$/.exec(ref);
  if (m === null) return null;
  let col = 0;
  for (const ch of (m[1] ?? "").toUpperCase()) col = col * 26 + (ch.charCodeAt(0) - 64);
  const row = parseInt(m[2] ?? "", 10);
  if (row < 1 || row > GRID_ROWS || col < 1 || col > GRID_COLS) return null;
  return { row: row - 1, col: col - 1 };
}

const ERRORS = new Set([
  "#NULL!",
  "#DIV/0!",
  "#VALUE!",
  "#REF!",
  "#NAME?",
  "#NUM!",
  "#N/A",
  "#GETTING_DATA",
  "#SPILL!",
  "#CALC!",
  "#DIM!",
]);

export function isErrorLiteral(s: string): boolean {
  return ERRORS.has(s.toUpperCase());
}

const NUMBER = /^[+-]?(\d{1,3}(,\d{3})+|\d+)?(\.\d+)?([eE][+-]?\d+)?%?$/;

/** Whether an input would be read as something other than text */
export function inputIsNotText(s: string): boolean {
  if (s === "") return false;
  if (s[0] === "=" || s[0] === "'") return true;
  const t = s.trim();
  const up = t.toUpperCase();
  if (up === "TRUE" || up === "FALSE" || isErrorLiteral(t)) return true;
  return /\d/.test(t) && NUMBER.test(t);
}

/** The input for a text value: as it is, or with a leading ' */
export function textInput(s: string): string {
  return inputIsNotText(s) ? "'" + s : s;
}

/** The text a number is typed as (Excel's 15 significant digits) */
export function numberInput(n: number): string {
  if (!Number.isFinite(n)) return "#NUM!";
  if (Object.is(n, -0)) return "0";
  const s = String(n);
  if (!/e/i.test(s) && s.replace(/[-.]/g, "").replace(/^0+/, "").length <= 15) return s;
  return String(Number(n.toPrecision(15)));
}

export function utf8Length(s: string): number {
  return new TextEncoder().encode(s).length;
}

// Excel's sheet name rules, as the server checks them (tensor_sheet_name_valid)
export function sheetNameValid(name: string): boolean {
  const chars = [...name];
  if (chars.length < 1 || chars.length > 31) return false;
  if (/[:\\/?*[\]]/.test(name)) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(name)) return false;
  if (name.trim() !== name) return false;
  if (name.startsWith("'") || name.endsWith("'")) return false;
  return name.toLowerCase() !== "history";
}

export const RESERVED_NAMES = ["거래", "지출", "수입", "예산", "사람", "월", "분류", "결제수단"];

// Excel's defined-name rules, as the server checks them (tensor_defined_name_valid)
export function definedNameValid(name: string): boolean {
  const chars = [...name];
  if (chars.length < 1 || chars.length > 255) return false;
  if (RESERVED_NAMES.includes(name)) return false;
  if (!/^[\p{L}_\\][\p{L}\p{N}_.\\]*$/u.test(name)) return false;
  if (/^[A-Za-z]{1,3}[0-9]+$/.test(name)) return false; // a cell
  if (/^[RrCc]$/.test(name)) return false;
  if (/^[Rr]([0-9]+)?([Cc]([0-9]+)?)?$/.test(name) || /^[Cc][0-9]+$/.test(name)) return false; // R1C1
  return true;
}
